import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plane, Plus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { tripsApi } from "@/lib/api";
import TripCard from "@/components/trips/TripCard";
import LoadingState from "@/components/shared/LoadingState";
import EmptyState from "@/components/shared/EmptyState";
import ErrorState from "@/components/shared/ErrorState";
import type { Trip } from "@/lib/types";
import { heldForMeetingsNote } from "./dashboardView";
import { splitByLifecycle } from "@/components/trips/tripStatusView";

export default function Dashboard() {
  const { data, isLoading, isError, error, refetch } = useQuery<{
    trips: Trip[];
    /** § G35 — itineraries withheld here because they belong to a meeting. */
    held_for_meetings?: number;
  }>({
    queryKey: ["trips"],
    queryFn: tripsApi.getAll,
  });

  if (isLoading) return <LoadingState message="Loading your trips..." />;

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Failed to load trips"}
        onRetry={() => refetch()}
      />
    );
  }

  const trips = data?.trips || [];
  // § G35 — the itineraries an organizer is holding for attendees are NOT theirs
  // and no longer sit here. Say so, and point at where they do live; a card that
  // vanishes without explanation is the next bug report.
  const heldNote = heldForMeetingsNote(data?.held_for_meetings);
  /*
   * Travel already taken drops below travel still ahead. Wade, 2026-08-24:
   * *"My TripMuscle New Orleans trip is past date and still shows ACTIVE."*
   * Six of his cards were finished trips sitting among the live ones, each
   * wearing a badge that says the product is working on it.
   *
   * They are MOVED, never hidden — a card that vanishes without explanation is
   * the next bug report (§ G35), and a traveller's own history is theirs.
   */
  const { upcoming, past } = splitByLifecycle(trips);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Your Trips</h1>
        <Button asChild>
          <Link to="/trips/new">
            <Plus className="h-4 w-4 mr-2" />
            New Trip
          </Link>
        </Button>
      </div>

      {trips.length === 0 ? (
        <EmptyState
          icon={Plane}
          title="No trips yet"
          description="Create your first trip to start monitoring flight prices."
          action={{ label: "Create Trip", href: "/trips/new" }}
        />
      ) : (
        <>
          {upcoming.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcoming.map((trip) => (
                <TripCard key={trip.id} trip={trip} />
              ))}
            </div>
          )}

          {past.length > 0 && (
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Travelled
                </h2>
                <span className="text-xs text-muted-foreground">
                  {past.length} trip{past.length === 1 ? "" : "s"} · fare watching ended when you
                  flew
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {past.map((trip) => (
                  <TripCard key={trip.id} trip={trip} />
                ))}
              </div>
            </section>
          )}

          {upcoming.length === 0 && past.length > 0 && (
            <EmptyState
              icon={Plane}
              title="Nothing coming up"
              description="Every trip you have is behind you. Create a new one to start watching fares again."
              action={{ label: "Create Trip", href: "/trips/new" }}
            />
          )}
        </>
      )}

      {heldNote && (
        <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <Users className="h-3.5 w-3.5" />
          {heldNote}
          <Link to="/events" className="font-medium text-foreground underline-offset-2 hover:underline">
            Open meetings
          </Link>
        </p>
      )}
    </div>
  );
}
